import { flow, getSnapshot, Instance, types, applySnapshot } from "mobx-state-tree";
import { SharedOrgConfig } from "@fidget/common/models/shared/sharedOrgConfig";
import { logger } from "@fidget/common/utils";
import { calculateWorkingHours, WorkingWeek } from "@fidget/common/utils/getWorkingHours";
import { servicesGetters } from "../utils";
import { getWheelhouseDownloadToken } from "@fidget/common/utils/urlMappingHelpers";

/**
 * Normalized working hours model
 * Note that this is a different model/format than the one stored in CurrentUserStore
 */
const WorkingHours = types.model("WorkingHours", {
  offsetSeconds: types.integer,
  durationSeconds: types.integer
});

const MaintenanceWindow = types.model("MaintenanceWindow", {
  from: types.number,
  to: types.number,
  message: types.string
});

export type EditableOrgConfigFields = {
  name?: string;
  baseUrl?: string;
  color?: string;
  callsDisabled?: boolean;
  headerWelcomeMessageEn?: string;
  headerWelcomeMessagePl?: string;
  phoneRegion?: string;
  showAdviserLastNameEnabled?: boolean;
  showAssignedAgentAvatarEnabled?: boolean;
  defaultAgentAvatarToken?: string;
};

export interface CloserBotOrgConfigFields {
  welcomeMessageEn?: string;
  welcomeMessagePl?: string;
  agreementEnabled: boolean;
  agreement?: string;
}

export interface OrgMessagesConfigFields {
  typingPreviewEnabled?: boolean;
  profanitiesCheckingEnabled?: boolean;
  keyboardShortcutsEnabled?: boolean;
  messageMaxAllowedCharactersForAgent?: number;
  messageMaxAllowedCharactersForClient?: number;
}

export interface NavbarMetricsFields {
  openedConversationsMetricEnabled?: boolean;
  waitingConversationsMetricEnabled?: boolean;
  averageResponseTimeMetricEnabled?: boolean;
}

export interface SlaFields {
  openedConversationsSlaEnabled?: boolean;
  waitingConversationsSlaEnabled?: boolean;
  averageResponseTimeSlaEnabled?: boolean;
  openedConversationsSlaRangeLowerBound?: number;
  waitingConversationsSlaRangeLowerBound?: number;
  averageResponseTimeSlaRangeLowerBound?: number;
  openedConversationsSlaRangeUpperBound?: number;
  waitingConversationsSlaRangeUpperBound?: number;
  averageResponseTimeSlaRangeUpperBound?: number;
}

type UpdateableFields =
  | EditableOrgConfigFields
  | OrgMessagesConfigFields
  | NavbarMetricsFields
  | SlaFields
  | CloserBotOrgConfigFields;

export const OrgConfig = types
  .compose(
    "OrgConfig",
    SharedOrgConfig,
    types.model({
      id: types.identifier,
      origin: types.array(types.string),
      welcomeMessagePl: types.maybe(types.string),
      welcomeMessageEn: types.maybe(types.string),
      welcomeMessageEnabled: types.boolean,
      headerWelcomeMessagePl: types.maybe(types.string),
      headerWelcomeMessageEn: types.maybe(types.string),
      agreementEnabled: types.boolean,
      languageLocale: types.maybe(types.string),
      baseUrl: types.string,
      autoAssignedLimit: types.number,
      meetingSlotSize: types.maybe(types.number),
      customerNotify: types.maybe(types.boolean),
      maintenanceWindows: types.array(MaintenanceWindow),
      workingHours: types.array(WorkingHours),
      zoneId: types.string,
      openedConversationsMetricEnabled: types.boolean,
      waitingConversationsMetricEnabled: types.boolean,
      averageResponseTimeMetricEnabled: types.boolean,
      openedConversationsSlaEnabled: types.maybe(types.boolean),
      waitingConversationsSlaEnabled: types.maybe(types.boolean),
      averageResponseTimeSlaEnabled: types.maybe(types.boolean),
      openedConversationsSlaRangeLowerBound: types.maybe(types.number),
      waitingConversationsSlaRangeLowerBound: types.maybe(types.number),
      averageResponseTimeSlaRangeLowerBound: types.maybe(types.number),
      openedConversationsSlaRangeUpperBound: types.maybe(types.number),
      waitingConversationsSlaRangeUpperBound: types.maybe(types.number),
      averageResponseTimeSlaRangeUpperBound: types.maybe(types.number),
      autoreassignEnabled: types.maybe(types.boolean),
      reportsEnabled: types.maybe(types.boolean),
      oauthEnabled: types.maybe(types.boolean),
      autoFollowOnUnassign: types.maybe(types.boolean),
      reassignOnLogoutEnabled: types.maybe(types.boolean),
      statusChangeOnCloseEnabled: types.maybe(types.boolean),
      snoozeOnRoomChange: types.maybe(types.boolean),
      autoCloseTimeoutSeconds: types.maybe(types.number),
      autoSnoozeTimeoutSeconds: types.maybe(types.number),
      messageMaxAllowedCharactersForAgent: types.maybe(types.number),
      contactEnquiryActionEnabled: types.maybe(types.boolean),
      meetingActionEnabled: types.maybe(types.boolean),
      webSpeechApiEnabled: types.maybe(types.boolean),
      defaultStatusAfterLogin: types.maybe(types.string),
      resetRoomViewOnClose: types.maybe(types.boolean),
      autoReassignOnWebsocketUnavailable: types.maybe(types.boolean),
      omniLink: types.maybe(types.string),
      omniEnabled: types.maybe(types.boolean),
      overrideStyles: types.maybe(types.string),
      fingerprintEnabled: types.boolean,
      externalIntegrationName: types.maybe(types.string),
    })
  )
  .views(servicesGetters)
  .actions((self) => {
    // TODO: clarify what to do with UserProfile.autoAssignedLimit
    const setAutoAssignedLimit = flow(function* (autoAssignedLimit: number) {
      try {
        const { id, ...config } = getSnapshot(self, false);
        const result = yield self.spinnerService.putConfig(id, {
          ...config,
          autoAssignedLimit
        });
        self.autoAssignedLimit = result.autoAssignedLimit;
      } catch (e) {
        logger.error({ message: "Couldn't set auto assigned limit", error: e });
        throw Error("Couldn't set auto assignedl imit");
      }
    });

    const setAutoSnoozeAndCloseTimeouts = flow(function* (
      autoSnoozeTimeoutSeconds: number | undefined,
      autoCloseTimeoutSeconds: number | undefined
    ) {
      try {
        const { id, ...config } = getSnapshot(self, false);
        const result = yield self.spinnerService.putConfig(id, {
          ...config,
          autoCloseTimeoutSeconds,
          autoSnoozeTimeoutSeconds
        });
        self.autoCloseTimeoutSeconds = result.autoCloseTimeoutSeconds;
        self.autoSnoozeTimeoutSeconds = result.autoSnoozeTimeoutSeconds;
      } catch (e) {
        logger.error({ message: "Couldn't update config", error: e });
        throw Error("Couldn't update config");
      }
    });

    const updateLogo = flow(function* (file: File) {
      try {
        const url = yield self.spinnerService.changeOrgLogo(file, self.id);
        self.logoToken = getWheelhouseDownloadToken(url);
        return self.logoToken;
      } catch (e) {
        logger.error({ message: "Couldn't update log", error: e });
        throw Error("Couldn't update logo");
      }
    });

    const deleteLogo = flow(function* () {
      try {
        yield self.spinnerService.deleteOrgLogo(self.id);
        self.logoToken = undefined;
      } catch (e) {
        logger.error({ message: "Couldn't delete log", error: e });
        throw Error("Couldn't delete logo");
      }
    });

    const updateCover = flow(function* (file: File) {
      try {
        const url = yield self.spinnerService.changeOrgCover(file, self.id);
        self.coverToken = getWheelhouseDownloadToken(url);
        return self.coverToken;
      } catch (e) {
        logger.error({ message: "Couldn't update cover", error: e });
        throw Error("Couldn't update cover");
      }
    });

    const deleteCover = flow(function* () {
      try {
        yield self.spinnerService.deleteOrgCover(self.id);
        self.coverToken = undefined;
      } catch (e) {
        logger.error({ message: "Couldn't delete cover", error: e });
        throw Error("Couldn't delete cover");
      }
    });

    const updateDefaultAgentAvatar = flow(function* (file: File) {
      try {
        const url = yield self.spinnerService.changeOrgAgentAvatar(file, self.id);
        self.defaultAgentAvatarToken = getWheelhouseDownloadToken(url);
        return self.defaultAgentAvatarToken;
      } catch (e) {
        logger.error({ message: "Couldn't update default avatar", error: e });
        throw Error("Couldn't update default avatar");
      }
    });

    const deleteDefaultAgentAvatar = flow(function* () {
      try {
        yield self.spinnerService.deleteOrgDefaultAgentAvatar(self.id);
        self.defaultAgentAvatarToken = undefined;
      } catch (e) {
        logger.error({ message: "Couldn't delete default avatar", error: e });
        throw Error("Couldn't delete default avatar");
      }
    });

    const update = flow(function* (data: UpdateableFields) {
      try {
        const { id, ...config } = getSnapshot(self);
        const responseData = yield self.spinnerService.putConfig(id, { ...config, ...data });
        applySnapshot(self, { id, ...config, ...responseData });
      } catch (e) {
        logger.error({ message: "Couldn't update organization config", error: e });
        throw Error("Couldn't update organization config");
      }
    });

    const updateWorkingHours = flow(function* (workingWeek: WorkingWeek) {
      try {
        const workingHours = calculateWorkingHours(workingWeek);
        const { id, ...config } = getSnapshot(self);
        const responseData = yield self.spinnerService.putConfig(id, {
          ...config,
          workingHours
        });
        applySnapshot(self, { id, ...config, workingHours, ...responseData });
      } catch (e) {
        logger.error({ message: "Couldn't update ", error: e });
        throw Error("Couldn't update working hours");
      }
    });

    return {
      updateLogo,
      deleteLogo,
      updateCover,
      deleteCover,
      updateDefaultAgentAvatar,
      deleteDefaultAgentAvatar,
      setAutoAssignedLimit,
      setAutoSnoozeAndCloseTimeouts,
      update,
      updateWorkingHours
    };
  })
  .preProcessSnapshot((snapshot) => snapshot && JSON.parse(JSON.stringify(snapshot)));

export type IOrgConfig = Instance<typeof OrgConfig>;
